import React from 'react';
import { Card, DonutChart, Flex, Metric, Text } from '@tremor/react';
import { useTranslation } from 'react-i18next';

function AudioEmotionSentimentCard({ fragment, ...rest }) {
    const { t } = useTranslation();

    // Handle missing fragment
    if (!fragment || fragment.status !== 'completed' || !fragment.result) {
        return (
            <Card className="max-h-full" data-testid="audio-emotion-sentiment-card" {...rest}>
                <div className="p-4">
                    <Text className="text-yellow-600">
                        {t('AudioEmotionSentiment.NotReady', 'Audio emotion analysis not ready')}
                    </Text>
                    <div className="mt-2 text-xs text-gray-500">
                        Status: {fragment?.status || 'missing'}<br/>
                        Has result: {fragment?.result ? 'yes' : 'no'}
                    </div>
                </div>
            </Card>
        );
    }

    try {
        const results = typeof fragment.result === 'string' ? JSON.parse(fragment.result) : fragment.result;
        
        if (!results || typeof results !== 'object') {
            return (
                <Card className="max-h-full" data-testid="audio-emotion-sentiment-card" {...rest}>
                    <div className="p-4">
                        <Text className="text-red-600">
                            {t('AudioEmotionSentiment.InvalidData', 'Invalid emotion analysis data')}
                        </Text>
                        <div className="mt-2 text-xs text-gray-500">
                            Data type: {typeof results}<br/>
                            Data: {JSON.stringify(results)}
                        </div>
                    </div>
                </Card>
            );
        }
        
        // Check for different possible data structures
        let emotionData = null;
        let duration = null;
        let segmentCount = 0;

        // Try overall_results.emotion_distribution (old structure)
        if (results.overall_results?.emotion_distribution) {
            emotionData = results.overall_results.emotion_distribution;
            duration = results.overall_results.total_duration_seconds;
            segmentCount = results.overall_results.total_segments;
        }
        // Try new structure: segments with emotions (in overall_results)
        else if (results.overall_results?.segments && Array.isArray(results.overall_results.segments)) {
            emotionData = calculateEmotionsFromSegments(results.overall_results.segments);
            duration = results.overall_results?.audio_info?.duration || results.overall_results?.processing_stats?.total_duration;
            segmentCount = results.overall_results.segments.length;
        }
        // Try segments at root level (fallback)
        else if (results.segments && Array.isArray(results.segments)) {
            emotionData = calculateEmotionsFromSegments(results.segments);
            duration = results.audio_info?.duration || results.processing_stats?.total_duration;
            segmentCount = results.segments.length;
        }
        // Try summary structure
        else if (results.summary?.emotions) {
            emotionData = results.summary.emotions;
            duration = results.audio_info?.duration;
            segmentCount = results.segments?.length || 0;
        }

        if (!emotionData) {
            return (
                <Card className="max-h-full" data-testid="audio-emotion-sentiment-card" {...rest}>
                    <div className="p-4">
                        <Text className="text-red-600">
                            {t('AudioEmotionSentiment.NoEmotionData', 'No emotion data found')}
                        </Text>
                        <div className="mt-2 text-xs text-gray-500">
                            Available keys: {Object.keys(results).join(', ')}<br/>
                            Overall results segments: {results.overall_results?.segments ? `${results.overall_results.segments.length} items` : 'missing'}<br/>
                            Root level segments: {results.segments ? `${results.segments.length} items` : 'missing'}<br/>
                            Summary: {results.summary ? 'exists' : 'missing'}<br/>
                            Time series: {results.time_series ? `${results.time_series.length} items` : 'missing'}
                        </div>
                    </div>
                </Card>
            );
        }

        // Use emotion data directly instead of calculating sentiments
        if (!emotionData || Object.keys(emotionData).length === 0) {
            return (
                <Card className="max-h-full" data-testid="audio-emotion-sentiment-card" {...rest}>
                    <div className="p-4">
                        <Text className="text-red-600">
                            {t('AudioEmotionSentiment.NoEmotionCalculated', 'Could not process emotion data')}
                        </Text>
                        <div className="mt-2 text-xs text-gray-500">
                            Emotion data: {JSON.stringify(emotionData)}
                        </div>
                    </div>
                </Card>
            );
        }

        return (
            <Card className="max-h-full" data-testid="audio-emotion-sentiment-card" {...rest}>
                <div className="p-4">
                    <Text className="font-medium text-gray-900 mb-2">
                        {t('AudioEmotionSentiment.Title', 'Audio Emotion Analysis')}
                    </Text>
                    
                    <Flex className="space-x-3 truncate mb-4" justifyContent="start" alignItems="baseline">
                        <Metric>{getDominantEmotion(emotionData, t)}</Metric>
                    </Flex>

                    <DonutChart
                        className="mt-4 max-h-32"
                        data={Object.entries(emotionData).map(([emotion, value]) => ({
                            name: formatEmotionName(emotion, t),
                            value: Math.max(0, Math.round(value * 1000) / 10) // Show one decimal place
                        })).filter(item => item.value > 0)}
                        category="value"
                        index="name"
                        colors={Object.keys(emotionData).map(emotion => getEmotionTremorColor(emotion))}
                        showAnimation={true}
                        variant="pie"
                        valueFormatter={(value) => `${value}%`}
                        noDataText={t('AudioEmotionSentiment.NoDataAvailable', 'No emotion data available')}
                    />

                    <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
                        {Object.entries(emotionData)
                            .sort(([,a], [,b]) => b - a) // Sort by percentage (highest to lowest)
                            .map(([emotion, value]) => (
                            <div key={emotion} className="flex items-center gap-2">
                                <div 
                                    className={`w-3 h-3 rounded ${getEmotionTailwindColor(emotion)}`}
                                ></div>
                                <span className="text-sm text-gray-700">
                                    {formatEmotionName(emotion, t)} ({Math.round(value * 1000) / 10}%)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        );
    } catch (error) {
        return (
            <Card className="max-h-full" data-testid="audio-emotion-sentiment-card" {...rest}>
                <div className="p-4">
                    <Text className="text-red-600">
                        {t('AudioEmotionSentiment.Error', 'Error processing sentiment data')}
                    </Text>
                    <div className="mt-2 text-xs text-gray-500">
                        Error: {error.message}<br/>
                        Stack: {error.stack?.substring(0, 200)}...<br/>
                        Fragment status: {fragment?.status}<br/>
                        Fragment result type: {typeof fragment?.result}
                    </div>
                </div>
            </Card>
        );
    }
}

// Helper functions for sentiment mapping
function formatEmotionName(emotion, t) {
    if (!emotion) return t('AudioEmotionSentiment.Emotions.Unknown', 'Unknown');
    
    const emotionKey = emotion.toLowerCase();
    const translationKey = `AudioEmotionSentiment.Emotions.${emotion.charAt(0).toUpperCase() + emotion.slice(1)}`;
    
    switch (emotionKey) {
        case 'happiness':
            return t('AudioEmotionSentiment.Emotions.Happiness', 'Happiness');
        case 'sadness':
            return t('AudioEmotionSentiment.Emotions.Sadness', 'Sadness');
        case 'anger':
            return t('AudioEmotionSentiment.Emotions.Anger', 'Anger');
        case 'fear':
            return t('AudioEmotionSentiment.Emotions.Fear', 'Fear');
        case 'surprise':
            return t('AudioEmotionSentiment.Emotions.Surprise', 'Surprise');
        case 'disgust':
            return t('AudioEmotionSentiment.Emotions.Disgust', 'Disgust');
        case 'neutral':
            return t('AudioEmotionSentiment.Emotions.Neutral', 'Neutral');
        default:
            return t('AudioEmotionSentiment.Emotions.Unknown', 'Unknown');
    }
}

function getDominantEmotion(emotionData, t) {
    let maxEmotion = 'neutral';
    let maxValue = 0;
    
    Object.entries(emotionData).forEach(([emotion, value]) => {
        if (value > maxValue) {
            maxValue = value;
            maxEmotion = emotion;
        }
    });
    
    return formatEmotionName(maxEmotion, t);
}

function calculateEmotionsFromSegments(segments) {
    if (!Array.isArray(segments) || segments.length === 0) {
        return null;
    }
    
    const emotionTotals = {};
    
    segments.forEach(segment => {
        // Look for emotion data in different possible locations
        let emotions = null;
        if (segment.emotions) {
            emotions = segment.emotions;
        } else if (segment.emotion_analysis) {
            emotions = segment.emotion_analysis;
        } else if (segment.dominant_emotion) {
            emotions = { [segment.dominant_emotion]: segment.confidence || 1 };
        }
        
        if (emotions) {
            Object.entries(emotions).forEach(([emotion, score]) => {
                emotionTotals[emotion] = (emotionTotals[emotion] || 0) + (typeof score === 'number' ? score : 1);
            });
        }
    });
    
    // Calculate total sum of all emotion values
    const totalEmotionSum = Object.values(emotionTotals).reduce((sum, value) => sum + value, 0);
    
    // Normalize to proportional percentages that sum to 100%
    const normalized = {};
    Object.entries(emotionTotals).forEach(([emotion, total]) => {
        normalized[emotion] = totalEmotionSum > 0 ? total / totalEmotionSum : 0;
    });
    
    return Object.keys(normalized).length > 0 ? normalized : null;
}

function getEmotionTremorColor(emotion) {
    const colorMap = {
        'happiness': 'green-500',   // green
        'sadness': 'blue-500',      // blue
        'anger': 'red-500',         // red
        'fear': 'purple-500',       // purple
        'surprise': 'amber-500',    // amber
        'disgust': 'lime-500',      // lime
        'neutral': 'gray-500',      // gray
    };
    return colorMap[emotion?.toLowerCase()] || 'gray-500';
}

function getEmotionTailwindColor(emotion) {
    const colorMap = {
        'happiness': 'bg-green-500',   // green
        'sadness': 'bg-blue-500',      // blue
        'anger': 'bg-red-500',         // red
        'fear': 'bg-purple-500',       // purple
        'surprise': 'bg-amber-500',    // amber
        'disgust': 'bg-lime-500',      // lime
        'neutral': 'bg-gray-500',      // gray
    };
    return colorMap[emotion?.toLowerCase()] || 'bg-gray-500';
}


function formatDuration(seconds) {
    if (!seconds) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
}

export default AudioEmotionSentimentCard;